import axios from 'axios';
import {useState} from 'react';

import {ProgressSpinner} from 'primereact/progressspinner';
import {getRecaptchaToken, isEmailValid} from '../utils';

import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {useTranslation} from '../hooks';
import './contactform.css';

const ContactForm = () => {
  const {t} = useTranslation();
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [mailSent, setMailSent] = useState({
    idle: true,
    animate: false,
    sent: false,
    failed: false,
    msg: '',
  });

  function validateEmail(e: React.FocusEvent<HTMLInputElement>): string | void {
    setEmailError('');

    if (!isEmailValid.test(e.target.value)) {
      return setEmailError(t('contactFormErrorEmail'));
    }

    return e.target.value;
  }

  function validateMessage(
    e: React.FocusEvent<HTMLTextAreaElement>,
  ): string | void {
    setMessageError('');
    const isEmpty = e.target.value.trim() === '';

    if (isEmpty) {
      return setMessageError(t('contactFormErrorTextArea'));
    }

    return e.target.value;
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (emailError || messageError) return;

    setMailSent({
      idle: true,
      animate: true,
      sent: false,
      failed: false,
      msg: '',
    });

    const recaptchaKey = await getRecaptchaToken();

    if (!recaptchaKey) {
      return setMailSent({
        idle: false,
        failed: true,
        animate: false,
        sent: false,
        msg: '',
      });
    }

    const formData = new FormData(e.target);

    formData.append('fullName', e.target.elements.fullName.value);
    formData.append('company', e.target.elements.company.value);
    formData.append('email', e.target.elements.email.value);
    formData.append('phone', e.target.elements.phone.value);
    formData.append('message', e.target.elements.message.value);
    formData.append('recaptchaKey', recaptchaKey);

    const dataObject = Object.fromEntries(formData);

    axios({
      method: 'POST',
      url: 'https://www.e-metal.com.tr/email/index.php',
      headers: {'content-type': 'application/json'},
      data: dataObject,
    })
      .then((result) => {
        if (result.status === 200) {
          setMailSent({
            idle: false,
            sent: true,
            animate: false,
            failed: false,
            msg: '',
          });
        } else {
          setMailSent({
            idle: false,
            sent: false,
            failed: true,
            animate: false,
            msg: result.data,
          });
        }
      })
      .catch((error) => {
        console.log(error);
        setMailSent({
          idle: false,
          failed: true,
          animate: false,
          sent: false,
          msg: error.message,
        });
      });

    e.target.reset();
  };

  return (
    <>
      {mailSent.idle && (
        <form
          className="form"
          name="contact-form"
          id="contact-form"
          method="post"
          action="/email/index.php"
          //action=""
          autoComplete="on"
          onSubmit={handleSubmit}>
          <InputText
            type="hidden"
            id="g-recaptcha-response"
            name="g-recaptcha-response"
          />
          <input type="hidden" name="action" value="validate_captcha" />

          <div className="form-group">
            <label hidden htmlFor="fullName">
              {t('contactFormNameInputPlaceholder')}
            </label>
            <InputText
              required
              type="text"
              className="form-control"
              id="fullName"
              name="fullName"
              placeholder={t('contactFormNameInputPlaceholder')}
            />
          </div>

          <div className="form-sections">
            <div className="form-section">
              <div className="form-group">
                <label hidden htmlFor="company">
                  {t('contactFormCompanyInputPlaceholder')}
                </label>
                <InputText
                  type="company"
                  className="form-control"
                  id="company"
                  name="company"
                  placeholder={t('contactFormCompanyInputPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label hidden htmlFor="email">
                  {t('contactFormEmailInputPlaceholder')}
                </label>
                <InputText
                  required
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  placeholder={t('contactFormEmailInputPlaceholder')}
                  onBlur={validateEmail}
                  onFocus={() => setEmailError('')}
                />
                {emailError && (
                  <small role="alert" className="text-error">
                    {emailError}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label hidden htmlFor="phone">
                  {t('contactFormPhoneInputPlaceholder')}
                </label>
                <InputText
                  type="number"
                  className="form-control"
                  id="phone"
                  name="phone"
                  placeholder={t('contactFormPhoneInputPlaceholder')}
                />
              </div>
            </div>
            <div className="form-section">
              <div className="form-group">
                <label hidden htmlFor="message">
                  {t('contactFormTextInputInputPlaceholder')}
                </label>
                <InputTextarea
                  placeholder={t('contactFormTextInputInputPlaceholder')}
                  required
                  className="form-control"
                  id="message"
                  name="message"
                  rows={5}
                  onBlur={validateMessage}
                  onFocus={() => setMessageError('')}></InputTextarea>
                {messageError && (
                  <small role="alert" className="text-error">
                    {messageError}
                  </small>
                )}
              </div>
            </div>
          </div>

          <Button
            severity="contrast"
            className="form-btn"
            aria-label={
              mailSent.animate ? t('sending') : t('contactFormSendButtonLabel')
            }
            label={
              mailSent.animate ? t('sending') : t('contactFormSendButtonLabel')
            }>
            {mailSent.animate && (
              <ProgressSpinner
                style={{width: '20px', height: '20px'}}
                strokeWidth="8"
                fill="var(--surface-ground)"
                animationDuration=".5s"
                aria-live="polite"
              />
            )}
          </Button>
        </form>
      )}
      {mailSent.sent && (
        <p role="alert" className="text-success">
          {t('contactFormSubmitSuccessMessage')}
        </p>
      )}

      {mailSent.failed && (
        <>
          <p role="alert" className="text-error">
            {t('contactFormSubmitFailureMessage')}
            <span
              role="button"
              className="btn-go-back"
              onClick={() =>
                setMailSent({
                  idle: true,
                  failed: false,
                  animate: false,
                  sent: false,
                  msg: '',
                })
              }>
              {t('contactFormReturnFormTitle')}
            </span>
          </p>
        </>
      )}
    </>
  );
};

export default ContactForm;
